import React__default, { useRef, useCallback, useEffect } from 'react';
import { Shape } from '../Shape/index.es.js';
import { getDimension } from '../BaseProps/index.es.js';
import styled from '@emotion/styled';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const getTrackedTouch = (containerRef, touches) => Array.from(touches).find((touch) => {
  var _a;
  return (_a = containerRef.current) == null ? void 0 : _a.contains(touch.target);
});
const calculateShiftAmount = (clientX, clientY, leftBoundingRect, topBoundingRect) => ({
  shiftLeft: clientX - leftBoundingRect,
  shiftTop: clientY - topBoundingRect
});
const Movable = (_a) => {
  var _b = _a, {
    movable = true,
    children,
    boundingRect
  } = _b, rest = __objRest(_b, [
    "movable",
    "children",
    "boundingRect"
  ]);
  const ref = useRef(null);
  const shiftLeft = useRef(0);
  const shiftTop = useRef(0);
  const isServerless = typeof window !== "undefined";
  const isTouchable = isServerless && "ontouchstart" in window;
  const startEvent = isTouchable ? "touchstart" : "mousedown";
  const moveEvent = isTouchable ? "touchmove" : "mousemove";
  const endEvent = isTouchable ? "touchend" : "mouseup";
  const move = useCallback((clientX, clientY, shiftLeft2, shiftTop2) => {
    const el = ref.current;
    if (el) {
      const left = clientX - shiftLeft2;
      const top = clientY - shiftTop2;
      const right = document.documentElement.clientWidth - left - el.clientWidth;
      const bottom = document.documentElement.clientHeight - top - el.clientHeight;
      const {
        top: topRect = 0,
        left: leftRect = 0,
        right: rightRect = 0,
        bottom: bottomRect = 0
      } = boundingRect || {};
      let leftStyle = `${left < leftRect ? leftRect : left}px`;
      let topStyle = `${top < topRect ? topRect : top}px`;
      if (right < rightRect) {
        leftStyle = `${document.documentElement.clientWidth - rightRect - el.clientWidth}px`;
      }
      if (bottom < bottomRect) {
        topStyle = `${document.documentElement.clientHeight - bottomRect - el.clientHeight}px`;
      }
      el.style.left = leftStyle;
      el.style.top = topStyle;
    }
  }, [boundingRect]);
  const onMove = useCallback((moveEvent2) => {
    if (ref.current) {
      moveEvent2.preventDefault();
      const el = ref.current;
      Array.from(el.children).forEach((child) => {
        child.style.pointerEvents = "none";
      });
      if (moveEvent2 instanceof TouchEvent) {
        const touch = getTrackedTouch(ref, moveEvent2.touches);
        if (!touch)
          return;
        move(touch == null ? void 0 : touch.clientX, touch == null ? void 0 : touch.clientY, shiftLeft.current, shiftTop.current);
      } else {
        el.onclick = null;
        move(moveEvent2 == null ? void 0 : moveEvent2.clientX, moveEvent2 == null ? void 0 : moveEvent2.clientY, shiftLeft.current, shiftTop.current);
      }
    }
  }, [move]);
  const onEnd = useCallback(() => {
    if (ref.current) {
      const el = ref.current;
      Array.from(el.children).forEach((child) => {
        child.style.pointerEvents = "auto";
      });
      document.removeEventListener(moveEvent, onMove);
      el.removeEventListener(endEvent, onEnd);
    }
  }, [onMove]);
  const onStart = useCallback((startEvent2) => {
    if (ref.current) {
      const el = ref.current;
      const { left, top } = el.getBoundingClientRect();
      if (startEvent2 instanceof TouchEvent) {
        const touch = getTrackedTouch(ref, startEvent2.touches);
        if (!touch)
          return;
        ({ shiftLeft: shiftLeft.current, shiftTop: shiftTop.current } = calculateShiftAmount(touch.clientX, touch.clientY, left, top));
      } else {
        ({ shiftLeft: shiftLeft.current, shiftTop: shiftTop.current } = calculateShiftAmount(startEvent2.clientX, startEvent2.clientY, left, top));
      }
      document.addEventListener(moveEvent, onMove);
      el.addEventListener(endEvent, onEnd);
    }
  }, [onEnd, onMove]);
  useEffect(() => {
    const el = ref.current;
    el == null ? void 0 : el.addEventListener(startEvent, onStart);
    return () => {
      el == null ? void 0 : el.removeEventListener(startEvent, onStart);
      document.removeEventListener(moveEvent, onMove);
      el == null ? void 0 : el.removeEventListener(endEvent, onEnd);
    };
  }, [movable, onStart]);
  if (!isServerless)
    return null;
  return /* @__PURE__ */ React__default.createElement(Wrapper, __spreadValues({
    ref,
    onDragStart: () => false
  }, rest), children);
};
const Wrapper = styled(Shape)((props) => `
  ${getDimension(props.top || "", "top: ", ";")}
  ${getDimension(props.bottom || "", "bottom: ", ";")}
  ${getDimension(props.left || "", "left: ", ";")}
  ${getDimension(props.right || "", "right: ", ";")}
  position: fixed;
  width: fit-content;
  height: fit-content;
  cursor: move;
  touch-action: none;
`);

export { Movable };
